import { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBag } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const NavLink = ({ href, children, id }) => (
  <a href={href} data-testid={`nav-${id}`} className="nav-link">{children}</a>
);

const AccountMenu = () => {
  const { user, logout, openAuth } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  if (!user) {
    return (
      <button onClick={() => openAuth("login")} data-testid="account-open-btn" className="nav-link">Account</button>
    );
  }

  const displayName = user.name || user.email.split("@")[0];

  return (
    <div className="relative" ref={menuRef}>
      <button onClick={() => setOpen((o) => !o)} data-testid="account-menu-btn" className="nav-link">
        {displayName}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0.001, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0.001, y: -6 }}
            transition={{ duration: 0.3 }} className="account-menu" data-testid="account-menu">
            <div className="px-5 py-3 border-b border-bone/10 space-y-0.5">
              {user.name && (
                <p className="text-xs font-serif text-bone tracking-wide" data-testid="account-menu-name">{user.name}</p>
              )}
              <p className="text-[11px] tracking-wide text-stone break-all font-mono" data-testid="account-menu-email">{user.email}</p>
            </div>
            <button 
              onClick={() => { setOpen(false); logout(); }} 
              data-testid="logout-btn"
              className="w-full text-left px-5 py-3 text-xs tracking-[0.25em] uppercase text-bone hover:bg-bone/5 transition-colors duration-300"
            >
              Sign out
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Header = () => {
  const { count, openCart } = useCart();
  return (
    <motion.header
      initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1.2, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50 header-glass" data-testid="site-header"
    >
      <div className="flex items-center justify-between px-5 md:px-14 h-20 md:h-24">
        
        {/* NAV SINISTRA: Visibile SOLO da schermi medi (desktop) */}
        <nav className="hidden md:flex gap-12 justify-start w-1/3">
          <NavLink href="#collection" id="collection">Collection</NavLink>
          <NavLink href="#philosophy" id="philosophy">Philosophy</NavLink>
        </nav>

        {/* LOGO CENTRALE */}
        <div className="flex justify-center md:w-1/3">
          <a 
            href="#hero" 
            onClick={(e) => {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="flex items-center gap-2 md:gap-3 group cursor-pointer" 
            data-testid="header-logo-link"
          >
            <img 
              src="/alkeon-pure.jpg" 
              alt="ALKEON" 
              className="h-6 md:h-7 w-auto object-contain rounded-sm transition-transform duration-500 group-hover:scale-105" 
            />
            <span className="font-serif text-bone tracking-[0.28em] text-base md:text-lg font-light group-hover:text-sand transition-colors duration-300">
              ALKEON
            </span>
          </a>
        </div>

        {/* NAV DESTRAM mobile mostra solo l'icona Carrello, su desktop mostra tutto */}
        <nav className="flex items-center justify-end gap-5 md:gap-12 md:w-1/3">
          <span className="hidden md:inline"><NavLink href="#atelier" id="atelier">Atelier</NavLink></span>
          <span className="hidden md:inline"><AccountMenu /></span>
          
          <button onClick={openCart} data-testid="cart-button" aria-label="Open cart"
            className="relative text-stone hover:text-bone transition-colors duration-300 p-1">
            <ShoppingBag className="w-5 h-5" strokeWidth={1.2} />
            <AnimatePresence>
              {count > 0 && (
                <motion.span key={count} initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.4, opacity: 0 }}
                  transition={{ duration: 0.3 }} data-testid="cart-count-badge"
                  className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-bone text-ink text-[10px] leading-4 text-center">
                  {count}
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </nav>

      </div>
    </motion.header>
  );
};

export default Header;